/**
 * Client wiring — auth core + http client. Mirrors wunderguide-web's
 * src/client/index.ts but scoped to the embed (no entitlements/user contexts
 * yet; just chat-and-voice). Storage is the iframe's own localStorage,
 * sandboxed to embed.wunderguide.com — that's intentional, every partner
 * embed gets its own session keyed by domain + the instance ID.
 */
import { createHttpClient } from '@wg/api-client';
import { createAuthCore } from '@wg/hooks/auth';
import config from '../../config/env.json';
import packageJson from '../../package.json';
import logger from '../utils/logger';

const APP_BUILD = '0';

const getOsVersion = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.userAgent || 'unknown';
};

const getSelectedLanguage = (): string => {
  // Locale comes from URL params (set by the bootstrap from navigator.language
  // on the parent site), so the embed honors the host page's language. Fall
  // back to navigator.language if for some reason the param is absent.
  if (typeof window !== 'undefined') {
    try {
      const p = new URLSearchParams(window.location.search);
      const fromUrl = p.get('locale');
      if (fromUrl) return fromUrl;
    } catch {
      /* noop */
    }
  }
  return typeof navigator !== 'undefined'
    ? navigator.language || 'en-US'
    : 'en-US';
};

const storage = {
  getString(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      console.error('[storage] getItem', key, err);
      return null;
    }
  },
  setString(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      console.error('[storage] setItem', key, err);
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.error('[storage] remove', key, err);
    }
  },
  getBoolean(key: string): boolean {
    try {
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  },
  setBoolean(key: string, value: boolean): void {
    try {
      localStorage.setItem(key, value ? 'true' : 'false');
    } catch {
      /* noop */
    }
  },
  clearAll(): void {
    try {
      localStorage.clear();
    } catch {
      /* noop */
    }
  },
};

// Storage key is scoped to the partner instance, so a single browser visiting
// embeds from multiple partners doesn't share a guest session across them.
// Critical for affiliate attribution: bookings made via partner B's embed
// must not inherit partner A's instance_id from a stale session.
const getInstanceId = (): string => {
  if (typeof window === 'undefined') return 'default';
  try {
    return (
      new URLSearchParams(window.location.search).get('instance') || 'default'
    );
  } catch {
    return 'default';
  }
};
const STORAGE_KEY = `auth_${getInstanceId()}`;

export const auth = createAuthCore({
  storage,
  logger,
  storageKey: STORAGE_KEY,
  getAppInfo: () => ({
    appName: 'wg-embedded-app',
    appVersion: packageJson.version,
    deviceOs: 'web',
    osVersion: getOsVersion(),
  }),
});

const { httpClient, unauthClient } = createHttpClient({
  baseURL: config.BASE_URL,
  userAgent: config.USER_AGENT,
  appId: 'wg-embedded-app',
  appVersion: packageJson.version,
  appBuild: APP_BUILD,
  osName: 'web',
  osVersion: getOsVersion(),
  getAuthToken: () => auth.getAuthToken(),
  tryRefreshAuthToken: () => auth.tryRefreshAuthToken(),
  getSelectedLanguage,
  logger,
  authedTimeoutMs: 20000,
  unauthedTimeoutMs: 20000,
});

// Same User-Agent header workaround as wunderguide-web (iOS Safari forbids it
// from being sent by JS; CORS preflight then fails on Caddy's allow-list).
for (const c of [httpClient, unauthClient]) {
  delete (c.defaults.headers as Record<string, unknown>)['User-Agent'];
  delete (c.defaults.headers.common as Record<string, unknown> | undefined)?.[
    'User-Agent'
  ];
}

auth.attachClients({ httpClient, unauthClient });

export { httpClient, unauthClient };
